import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, UntypedFormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { FieldConfig, RadioComponent } from 'hig-ng-lib';
import { Subscription } from 'rxjs';
import { AppConfig } from '../../../../../config/app-config';
import {
    CLAIMANT_DRIVER_NAME_ERROR,
    CLAIMANT_NAME_PATTERN_ERROR,
    CLAIM_INPUT_ERROR,
    CLAIM_PATTERN_ERROR,
    CLAIM_SELECT_ERROR
} from '../../../../../treo.properties';
import { ListClaimDetails } from '../../../../views/claims/claims.model';
import { ClaimsService } from '../../../../views/claims/claims.service';
import { ApiService } from '../../../services/api.service';
import { LoaderService } from '../../../services/loading.service';
import { MenuService } from '../../../services/menu.service';
import { SharedService } from '../../../services/shared.service';

interface MessageFieldConfigType {
    name: string;
    showField: boolean;
}
interface ErrorMsgType {
    messageText: string;
    messageType: string;
}
@Component({
    selector: 'app-claim-menu',
    templateUrl: './claim-menu.component.html',
    standalone: false
})
export class ClaimMenuComponent implements OnInit, AfterViewInit, OnDestroy {
    public JSON = JSON;
    claimForm: FormGroup;
    inputString: string;
    selectedClaimRadio: string;
    claimSearchSubscription$: Subscription;
    currentNumberSubscription$: Subscription;
    orgnztnId: string;
    currentHeaderTabSubscription$: Subscription;
    currentHeaderTab: string = '';
    headerTabChanged: boolean = false;
    @ViewChild('claimMenuRadioTemplate') claimMenuRadioTemplate: RadioComponent;

    searchField: FieldConfig = {
        type: 'input',
        showField: true,
        name: 'searchField',
        subType: 'text',
        validations: [
            {
                name: 'required',
                message: CLAIM_INPUT_ERROR
            }
        ]
    };

    constructor(
        private fb: UntypedFormBuilder,
        private loaderService: LoaderService,
        private claimsService: ClaimsService,
        private apiService: ApiService,
        private SharedService: SharedService,
        private router: Router,
        private menuService: MenuService,
        private cdr: ChangeDetectorRef
    ) {}
    claimMenuRadioConfig = AppConfig.claimMenuRadioConfig;
    claimMenuButtonClearConfig = AppConfig.claimMenuButtonClearConfig;
    claimMenuButtonSearchConfig = AppConfig.claimMenuButtonSearchConfig;
    messageFieldConfig: MessageFieldConfigType = {
        name: 'searchErrorMsg',
        showField: true
    };
    errorMsg: ErrorMsgType = {
        messageText: '',
        messageType: 'error'
    };

    ngAfterViewInit(): void {
        this.currentNumberSubscription$ = this.SharedService.currentNumber.subscribe((res: string) => {
            if (res) {
                this.orgnztnId = res;
            }
        });
        this.currentHeaderTabSubscription$ = this.SharedService.currentLink.subscribe((res: any) => {
            this.headerTabChanged = true;
            this.clearData();
        });
        this.claimMenuRadioTemplate.field = this.claimMenuRadioConfig;
    }

    ngOnInit() {
        this.claimForm = this.fb.group({
            claimRadio: [''],
            searchField: ['']
        });

        // Trigger change detection after form initialization
        setTimeout(() => {
            this.cdr.detectChanges();
        });
    }

    get claimRadioValue(): string {
        return this.claimForm?.controls?.['claimRadio']?.value || '';
    }

    checkStringWithRegex(inputString: string): boolean {
        const regex = /^\s*[a-zA-Z0-9]{3}\s*[a-zA-Z]{1,2}\s*\d{5}\s*$/;
        return regex.test(inputString);
    }

    checkNoSpecialCharacters(input: string): boolean {
        const noSpecialCharRegex = /^[a-zA-Z0-9 ]*$/;
        return noSpecialCharRegex.test(input);
    }

    search() {
        this.errorMsg.messageText = '';
        if (
            this.claimForm.controls['searchField'].value !== '' &&
            this.claimForm.controls['searchField'].value !== null
        ) {
            if (this.claimForm.controls['claimRadio'].value === 'claimNumber') {
                if (this.checkStringWithRegex(this.claimForm.controls['searchField'].value)) {
                    this.fetchClaimDetails();
                } else {
                    this.errorMsg.messageText = CLAIM_PATTERN_ERROR;
                }
            } else {
                if (this.checkNoSpecialCharacters(this.claimForm.controls['searchField'].value)) {
                    this.fetchClaimantDetails();
                    this.menuService.menuHeading.next(AppConfig.claimantPageHeadingConfig);
                } else {
                    this.errorMsg.messageText = CLAIMANT_NAME_PATTERN_ERROR;
                }
            }
        } else {
            this.setErrorMsg();
        }
        this.claimForm.controls['claimRadio'].valueChanges.subscribe((selectedValue) => {
            if (selectedValue === 'claimNumber' || selectedValue === 'claimantName') this.errorMsg.messageText = '';
        });
    }

    private setErrorMsg() {
        if (this.claimForm.controls['claimRadio'].value === 'claimNumber')
            this.errorMsg.messageText = CLAIM_INPUT_ERROR;
        else if (this.claimForm.controls['claimRadio'].value === 'claimantName')
            this.errorMsg.messageText = CLAIMANT_DRIVER_NAME_ERROR;
        else this.errorMsg.messageText = CLAIM_SELECT_ERROR;
    }
    clearData() {
        let selectedRadio = this.claimForm.controls['claimRadio'].value;
        this.errorMsg.messageText = '';
        this.claimForm.reset();
        this.claimForm.clearValidators();
        if (!this.headerTabChanged) this.claimForm.controls['claimRadio'].setValue(selectedRadio);
        this.headerTabChanged = false;
    }

    getApplicationId() {
        const encodedId = this.loaderService.getItem('appAccountDetailId');
        if (!encodedId) return null;

        const decodedId = encodedId;
        return Number(decodedId) === 0 ? null : decodedId;
    }

    fetchClaimDetails() {
        let userId = this.loaderService.getItem('userId');
        let payload = {
            claimInfo: {
                claimNumber: this.claimForm.controls['searchField'].value,
                orgnztnId: this.loaderService.getItem('orgId'),
                applicationAccntDtlId: this.getApplicationId()
            },
            userId: userId,
            claimDetailType: 'CLAIM_MENU_BASIC_SEARCH',
            claimType: 'HARTFORD',
            actionType: 'RETRIEVE'
        };
        this.claimSearchSubscription$ = this.claimsService.searchClaimDetailByClaimNumber(payload).subscribe({
            next: (resp: ListClaimDetails) => {
                this.SharedService.setclaimDetails(resp.listClaimDetails[0].claims[0]);
                this.loaderService.setItem('claimDetails', JSON.stringify(resp.listClaimDetails[0].claims[0]));
                const getClaimNumber = resp.listClaimDetails[0].claims[0].claimNumber.split(' ').join('');
                this.loaderService.hideLoader();
                this.router.navigate([`claims/claim-detail/${getClaimNumber}`]);
                this.clearData();
            },
            error: (error) => {
                this.errorMsg.messageText = error.error.errorMessage;
                this.loaderService.hideLoader();
            }
        });
    }

    fetchClaimantDetails() {
        let userId = this.loaderService.getItem('userId');
        let payload = {
            claimInfo: {
                claimantName: this.claimForm.controls['searchField'].value,
                orgnztnId: this.loaderService.getItem('orgId')
            },
            userId: userId,
            claimDetailType: 'CLAIM_MENU_BASIC_SEARCH',
            claimType: 'HARTFORD',
            actionType: 'RETRIEVE'
        };
        this.loaderService.showLoader();
        this.claimSearchSubscription$ = this.claimsService.searchClaimDetailByClaimNumber(payload).subscribe({
            next: (resp: any) => {
                this.loaderService.hideLoader();
                this.SharedService.setclaimantDetails(resp.listClaimDetails[0].claims[0]);
                payload['accessedFrom'] = 'account-home';
                payload['listType'] = 'NameList';
                payload['filterId'] = resp.claimAllFiltersCriteria?.claimFilterCriteria?.filterId;
                this.router.navigate([`claims/claim-lists`, payload]);
            },
            error: (error) => {
                this.errorMsg.messageText = error.error.message;
                this.loaderService.hideLoader();
            }
        });
    }

    ngOnDestroy() {
        if (this.currentNumberSubscription$) this.currentNumberSubscription$.unsubscribe();
        if (this.claimSearchSubscription$) this.claimSearchSubscription$.unsubscribe();
    }
}
